/**
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops 依頼詳細のモーダル群（バグ起票 / 差戻し / 解決確認）をまとめるコンテナ。差戻し（理由 + メッセージ）・解決の送信ミューテーションと楽観的ステート更新（依頼者への差戻し=blocked、解決=closed）を持ち、失敗時は元ステートへロールバックする。開閉は詳細ページ（ボタン群と共有）が制御し、ここへ渡す
 * @graph-connects useSendBackOpsRequest [calls] 差戻し送信（理由 + メッセージを記録、state=blocked + 起票スレッド返信）
 * @graph-connects useSetOpsRequestState [calls] 解決確認モーダルからの解決（closed + 完了通知）
 */
import { useEffect, type ReactNode } from 'react';
import type { OpsRequest, OpsState } from '../../../types/ops.js';
import { useSendBackOpsRequest, useSetOpsRequestState } from '../../../hooks/useOps.js';
import { OpsBugTicketModal } from './OpsBugTicketModal.js';
import type { OpsSendBackReason } from '@cortex/ops-core';
import { OpsSendBackModal } from './OpsSendBackModal.js';
import { OpsCloseReportModal } from './OpsCloseReportModal.js';

/** OpsDetailModals の props。開閉状態は詳細ページ（アクションボタンと共有）が制御する。 */
export interface OpsDetailModalsProps {
  /** 対象依頼。 */
  request: OpsRequest;
  /** 編集可能（Firestore 発番）か。false=legacy はミューテーションを弾く。 */
  editable: boolean;
  /** 現在の進捗ステート（楽観更新のロールバック元）。 */
  state: OpsState;
  /** 楽観的にステートを切り替える（詳細ページの state を更新＝バッジ/トグル反映）。 */
  onStateChange: (next: OpsState) => void;
  /** バグ起票モーダルの開閉。 */
  bugTicketOpen: boolean;
  /** バグ起票モーダルを閉じる。 */
  onBugTicketClose: () => void;
  /** 差戻しモーダルの開閉。 */
  sendBackOpen: boolean;
  /** 差戻しモーダルを閉じる。 */
  onSendBackClose: () => void;
  /** 解決確認モーダルの開閉。 */
  confirmingClose: boolean;
  /** 解決確認モーダルの開閉を切り替える。 */
  onConfirmingCloseChange: (value: boolean) => void;
}

/**
 * 詳細ページのモーダル群（バグ起票 / 差戻し / 解決確認）をまとめて描画するコンテナ。差戻し・解決の送信は
 * ここで mutation を発火し、楽観的にステートを切り替え、失敗時は元へ戻す（承認済みテキストを無言で失わせない）。
 * @graph-connects useSendBackOpsRequest [calls] 差戻し送信
 * @graph-connects useSetOpsRequestState [calls] 解決確認からの解決
 */
export function OpsDetailModals({
  request,
  editable,
  state,
  onStateChange,
  bugTicketOpen,
  onBugTicketClose,
  sendBackOpen,
  onSendBackClose,
  confirmingClose,
  onConfirmingCloseChange,
}: OpsDetailModalsProps): ReactNode {
  const setStateMutation = useSetOpsRequestState();
  const sendBackMutation = useSendBackOpsRequest();
  const { reset: resetSetState } = setStateMutation;
  const { reset: resetSendBack } = sendBackMutation;

  // モーダルを開くたびに直前の失敗表示（isError）を消す（escalate の openEscalateConfirm と同じ reset-on-open）。
  // reset を怠ると、失敗 → キャンセル → 再オープンで古いエラーバナーが残る。mutation.reset は参照安定。
  useEffect(() => {
    if (confirmingClose) resetSetState();
  }, [confirmingClose, resetSetState]);
  useEffect(() => {
    if (sendBackOpen) resetSendBack();
  }, [sendBackOpen, resetSendBack]);

  // 解決確認モーダルで確定したときの解決。承認済みの対応結論（null=報告なし）を completedAt と一緒に送る。
  // 楽観的に closed 表示へ切り替え、成功時のみモーダルを閉じる。失敗時は元ステートへ戻し、モーダルは開いたまま。
  const resolveWithReport = (closeReport: string | null): void => {
    if (!editable) return;
    const previous = state;
    onStateChange('closed');
    setStateMutation.mutate(
      { id: request.id, state: 'closed', closeReport },
      {
        onSuccess: () => {
          onConfirmingCloseChange(false);
        },
        onError: () => {
          onStateChange(previous);
        },
      },
    );
  };

  // 差戻しモーダルで送信を確定したときの差戻し。state=blocked へ楽観更新し、成功時のみ閉じる。失敗は元へ戻す。
  const sendBack = (reason: OpsSendBackReason, message: string): void => {
    if (!editable) return;
    const previous = state;
    onStateChange('blocked');
    sendBackMutation.mutate(
      { id: request.id, reason, message },
      {
        onSuccess: () => {
          onSendBackClose();
        },
        onError: () => {
          onStateChange(previous);
        },
      },
    );
  };

  return (
    <>
      <OpsBugTicketModal
        requestId={request.id}
        requestText={`${request.background}\n${request.requested}`}
        linkedBugKeys={request.relatedTickets
          .filter((tk) => tk.target === 'backlog')
          .map((tk) => tk.ref)}
        open={bugTicketOpen}
        onClose={onBugTicketClose}
      />
      <OpsSendBackModal
        assigneeName={request.assigneeName}
        open={sendBackOpen}
        onClose={onSendBackClose}
        onSend={sendBack}
        isSending={sendBackMutation.isPending}
        sendFailed={sendBackMutation.isError}
      />
      <OpsCloseReportModal
        requestId={request.id}
        requestCategory={request.category}
        open={confirmingClose}
        onClose={() => {
          onConfirmingCloseChange(false);
        }}
        onResolve={resolveWithReport}
        isResolving={setStateMutation.isPending}
        resolveFailed={setStateMutation.isError}
      />
    </>
  );
}
